import Link from "next/link"

import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  ListTableCell,
  ListTableRow,
} from "@coval/ui"
import { Archive, MoreVertical, Pencil, Trash2 } from "lucide-react"

import type { WorkspaceResource } from "@/types_api/backend/transport/list_workspaces_response"

interface WorkspaceRowProps {
  workspace: WorkspaceResource
  isAdmin: boolean
  settingsHref?: string
  onRename: (workspaceItem: WorkspaceResource) => void
  onArchive: (workspaceItem: WorkspaceResource) => void
  onDelete: (workspaceItem: WorkspaceResource) => void
}

export function WorkspaceRow({
  workspace,
  isAdmin,
  settingsHref,
  onRename,
  onArchive,
  onDelete,
}: WorkspaceRowProps) {
  const isDefault = workspace.workspace_type === "DEFAULT"
  const isArchived = workspace.status === "ARCHIVED"
  const canManage = isAdmin && !isDefault
  const canOpenSettings = Boolean(settingsHref && !isArchived)

  return (
    <ListTableRow
      data-testid={`workspace-row-${workspace.id}`}
      className={canOpenSettings ? "relative cursor-pointer" : undefined}
    >
      <ListTableCell className="min-w-0">
        <div className="truncate font-medium text-coval-text">
          {settingsHref && canOpenSettings ? (
            <Link
              href={settingsHref}
              className="rounded-sm transition-colors after:absolute after:inset-0 after:z-10 after:rounded-md hover:text-coval-link focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-coval-info-border"
            >
              {workspace.display_name}
            </Link>
          ) : (
            workspace.display_name
          )}
        </div>
      </ListTableCell>
      <ListTableCell className="text-coval-text-muted">
        {isDefault ? "Default" : "Custom"}
      </ListTableCell>
      <ListTableCell>
        <Badge variant={isArchived ? "warning" : "success"}>
          {isArchived ? "Archived" : "Active"}
        </Badge>
      </ListTableCell>
      <ListTableCell className="text-right">
        {canManage && (
          <div className="relative z-20 inline-flex">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  icon
                  tooltip={`Actions for ${workspace.display_name}`}
                  data-testid={`workspace-actions-${workspace.id}`}
                >
                  <MoreVertical className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {workspace.status === "ACTIVE" && (
                  <>
                    <DropdownMenuItem
                      onSelect={() => onRename(workspace)}
                      data-testid={`workspace-rename-${workspace.id}`}
                    >
                      <Pencil className="size-4" />
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => onArchive(workspace)}
                      data-testid={`workspace-archive-${workspace.id}`}
                    >
                      <Archive className="size-4" />
                      Archive
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem
                  destructive
                  onSelect={() => onDelete(workspace)}
                  data-testid={`workspace-delete-${workspace.id}`}
                >
                  <Trash2 className="size-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </ListTableCell>
    </ListTableRow>
  )
}
